// Progressive translation module
// Automatically translates page content for non-English language URLs
class ProgressiveTranslator {
    constructor() {
        this.language = null;
        this.cache = {};
        this.translatedNodes = new WeakSet();
        this.isTranslating = false;
        this.batchQueue = [];
        this.batchTimeout = null;
        this.loadingIndicator = null;
        // Selectors to skip (don't translate these elements)
        this.skipSelectors = [
            'script', 'style', 'code', 'pre', 'kbd', 'var', 'samp',
            'noscript', 'iframe', 'object', 'embed', 'svg', 'math',
            '[data-no-translate]', '.no-translate', '.notranslate',
            'input', 'textarea', 'select', 'option',
            '[contenteditable="true"]'
        ];
        // Attributes to translate
        this.translatableAttributes = ['alt', 'title', 'placeholder', 'aria-label'];
        this.detectLanguage();
        if (this.language && this.language !== 'en') {
            this.init();
        }
    }
    detectLanguage() {
        // Extract language from URL path (e.g., /es/, /de/)
        const match = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/i);
        if (match) {
            this.language = match[1].toLowerCase();
        }
        // Also check for data attribute on html element
        const htmlLang = document.documentElement.lang;
        if (htmlLang && htmlLang !== 'en' && htmlLang.length === 2) {
            this.language = htmlLang;
        }
    }
    init() {
        // Load cached translations from sessionStorage
        this.loadCache();
        // Create loading indicator
        this.createLoadingIndicator();
        // Start translation after DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.translatePage());
        }
        else {
            this.translatePage();
        }
        // Observe DOM changes for dynamic content
        this.observeMutations();
    }
    createLoadingIndicator() {
        this.loadingIndicator = document.createElement('div');
        this.loadingIndicator.id = 'translation-indicator';
        this.loadingIndicator.innerHTML = `
      <div class="translation-indicator__content">
        <svg class="translation-indicator__spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/>
        </svg>
        <span>Translating...</span>
      </div>
    `;
        this.loadingIndicator.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 14px;
      z-index: 9999;
      opacity: 0;
      transform: translateY(10px);
      transition: opacity 0.3s, transform 0.3s;
      display: flex;
      align-items: center;
      gap: 10px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
        const style = document.createElement('style');
        style.textContent = `
      #translation-indicator.visible {
        opacity: 1;
        transform: translateY(0);
      }
      .translation-indicator__content {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .translation-indicator__spinner {
        width: 18px;
        height: 18px;
        animation: translator-spin 1s linear infinite;
      }
      @keyframes translator-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
        document.head.appendChild(style);
        document.body.appendChild(this.loadingIndicator);
    }
    showLoading() {
        if (this.loadingIndicator) {
            this.loadingIndicator.classList.add('visible');
        }
    }
    hideLoading() {
        if (this.loadingIndicator) {
            this.loadingIndicator.classList.remove('visible');
        }
    }
    loadCache() {
        try {
            const cached = sessionStorage.getItem(`translations_${this.language}`);
            if (cached) {
                this.cache = JSON.parse(cached);
            }
        }
        catch (e) {
            // Ignore storage errors
        }
    }
    saveCache() {
        try {
            // Limit cache size to prevent storage overflow
            const entries = Object.entries(this.cache);
            if (entries.length > 1000) {
                // Keep only the 500 most recent
                this.cache = Object.fromEntries(entries.slice(-500));
            }
            sessionStorage.setItem(`translations_${this.language}`, JSON.stringify(this.cache));
        }
        catch (e) {
            // Ignore storage errors
        }
    }
    shouldSkipNode(node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node;
            for (const selector of this.skipSelectors) {
                if (element.matches(selector)) {
                    return true;
                }
            }
        }
        return false;
    }
    shouldSkipText(text) {
        // Skip empty or whitespace-only
        if (!text || !text.trim())
            return true;
        // Skip very short strings (likely punctuation or numbers)
        if (text.trim().length < 2)
            return true;
        // Skip if only numbers, symbols, or currency
        if (/^[\d\s\$\€\£\¥\.\,\%\-\+\/\*\(\)\[\]\{\}\:\;\@\#\!\?\&\=\_\|\\\'\"]+$/.test(text))
            return true;
        // Skip URLs and emails
        if (/^https?:\/\/|^www\.|@.*\.\w{2,}$/i.test(text.trim()))
            return true;
        // Skip code-like content
        if (/^\{.*\}$|^\[.*\]$|^<.*>$/.test(text.trim()))
            return true;
        return false;
    }
    extractTextNodes(root = document.body) {
        const textNodes = [];
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => {
                // Skip if already translated
                if (this.translatedNodes.has(node)) {
                    return NodeFilter.FILTER_REJECT;
                }
                // Skip if parent should be skipped
                if (node.parentElement && this.shouldSkipNode(node.parentElement)) {
                    return NodeFilter.FILTER_REJECT;
                }
                // Skip if text should be skipped
                const text = node.textContent || '';
                if (this.shouldSkipText(text)) {
                    return NodeFilter.FILTER_REJECT;
                }
                return NodeFilter.FILTER_ACCEPT;
            }
        });
        let node;
        while ((node = walker.nextNode())) {
            const text = (node.textContent || '').trim();
            if (text) {
                textNodes.push({ text, node });
            }
        }
        return textNodes;
    }
    extractAttributes(root = document.body) {
        const attributes = [];
        const elements = root.querySelectorAll('*');
        elements.forEach((element) => {
            if (this.shouldSkipNode(element))
                return;
            for (const attr of this.translatableAttributes) {
                const value = element.getAttribute(attr);
                if (value && !this.shouldSkipText(value) && !this.translatedNodes.has(element)) {
                    attributes.push({ text: value, element, attr });
                }
            }
        });
        return attributes;
    }
    async translateBatch(texts) {
        if (!texts.length || !this.language)
            return texts;
        // Check cache first
        const uncachedTexts = [];
        const uncachedIndexes = [];
        const results = new Array(texts.length);
        texts.forEach((text, index) => {
            const cached = this.cache[text];
            if (cached) {
                results[index] = cached;
            }
            else {
                uncachedTexts.push(text);
                uncachedIndexes.push(index);
            }
        });
        // Translate uncached texts
        if (uncachedTexts.length > 0) {
            try {
                const response = await fetch('/api/translate-batch', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        texts: uncachedTexts,
                        targetLang: this.language,
                        source: 'progressive'
                    }),
                });
                if (response.ok) {
                    const data = await response.json();
                    // Map results back and cache them
                    data.translations.forEach((translation, i) => {
                        const originalIndex = uncachedIndexes[i];
                        const originalText = uncachedTexts[i];
                        results[originalIndex] = translation;
                        // Cache the translation
                        if (translation !== originalText) {
                            this.cache[originalText] = translation;
                        }
                    });
                    // Save updated cache
                    this.saveCache();
                }
            }
            catch (error) {
                console.error('Translation error:', error);
                // Return original texts on error
                uncachedIndexes.forEach((index, i) => {
                    results[index] = uncachedTexts[i];
                });
            }
        }
        return results;
    }
    async translatePage() {
        if (this.isTranslating || !this.language)
            return;
        this.isTranslating = true;
        try {
            // Extract text nodes
            const textNodes = this.extractTextNodes();
            const attributes = this.extractAttributes();
            // Combine all texts
            const allTexts = [
                ...textNodes.map(n => n.text),
                ...attributes.map(a => a.text)
            ];
            if (allTexts.length === 0) {
                this.isTranslating = false;
                return;
            }
            // Check how many need API translation (not cached)
            const uncachedCount = allTexts.filter(t => !this.cache[t]).length;
            // Only show loading if we need to fetch translations
            if (uncachedCount > 0) {
                this.showLoading();
            }
            // Deduplicate texts for efficiency
            const uniqueTexts = [...new Set(allTexts)];
            // Translate in batches
            const batchSize = 50;
            const translationMap = new Map();
            for (let i = 0; i < uniqueTexts.length; i += batchSize) {
                const batch = uniqueTexts.slice(i, i + batchSize);
                const translations = await this.translateBatch(batch);
                batch.forEach((text, index) => {
                    translationMap.set(text, translations[index]);
                });
            }
            // Apply translations to text nodes
            textNodes.forEach(({ text, node }) => {
                const translation = translationMap.get(text);
                if (translation && translation !== text && node.textContent) {
                    node.textContent = node.textContent.replace(text, translation);
                    this.translatedNodes.add(node);
                }
            });
            // Apply translations to attributes
            attributes.forEach(({ text, element, attr }) => {
                const translation = translationMap.get(text);
                if (translation && translation !== text) {
                    element.setAttribute(attr, translation);
                    this.translatedNodes.add(element);
                }
            });
            // Mark page as translated
            document.documentElement.setAttribute('data-translated', this.language);
        }
        catch (error) {
            console.error('Page translation error:', error);
        }
        this.hideLoading();
        this.isTranslating = false;
    }
    observeMutations() {
        const observer = new MutationObserver((mutations) => {
            let hasNewContent = false;
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) {
                            hasNewContent = true;
                        }
                    });
                }
            });
            if (hasNewContent) {
                // Debounce translation for dynamically added content
                if (this.batchTimeout) {
                    clearTimeout(this.batchTimeout);
                }
                this.batchTimeout = setTimeout(() => {
                    this.translatePage();
                }, 100);
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
    // Public method to manually trigger translation
    translate() {
        this.translatePage();
    }
    // Public method to get current language
    getLanguage() {
        return this.language;
    }
}
// Initialize translator
let translator = null;
export function initTranslator() {
    if (!translator) {
        translator = new ProgressiveTranslator();
    }
    return translator;
}
export default initTranslator;
